import {ChangeDetectionStrategy, Component, Input} from '@angular/core';
import {CommonModule} from '@angular/common';
import {RecipePreview, RecipePreviewStep} from './recipe-preview.models';
import {parseRecipeStepText, RecipeTextSegment} from './recipe-step-text';

const STEP_TYPE_LABELS: Record<string, string> = {
  instruction: 'Zubereitung',
  cook: 'Kochen',
  wait: 'Warten',
  serve: 'Servieren',
};

const STEP_TYPE_BADGE_CLASSES: Record<string, string> = {
  instruction: 'badge-ghost',
  cook: 'badge-warning',
  wait: 'badge-info',
  serve: 'badge-success',
};

const COOK_DEVICE_LABELS: Record<string, string> = {
  pan: 'Pfanne',
  oven: 'Ofen',
  airfryer: 'Heissluftfritteuse',
};

const WAIT_LOCATION_LABELS: Record<string, string> = {
  room: 'Raumtemperatur',
  fridge: 'Kühlschrank',
  freezer: 'Gefrierfach',
};

/**
 * Shared recipe preview card. Renders a recipe from a `RecipePreview` — the shape
 * the dialog assistant authors (`create_recipe`) and the recipe importer produces —
 * both while it is still an unsaved draft and after it has been saved (same shape).
 * Presentational only: no data fetching, no save action; hosts wire those around it.
 *
 * Step text is never trusted as HTML — see `parseRecipeStepText`.
 */
@Component({
  selector: 'ui-recipe-preview',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './ui-recipe-preview.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UiRecipePreviewComponent {
  @Input({required: true}) recipe!: RecipePreview;

  stepTypeLabel(step: RecipePreviewStep): string {
    return STEP_TYPE_LABELS[step.type] ?? step.type;
  }

  stepBadgeClass(step: RecipePreviewStep): string {
    return STEP_TYPE_BADGE_CLASSES[step.type] ?? 'badge-ghost';
  }

  stepSegments(step: RecipePreviewStep): RecipeTextSegment[] {
    return parseRecipeStepText(step.text);
  }

  /** Nice-to-have subtitle line summarizing the cook/wait-specific fields — omitted
   *  entirely (returns null) when a step carries none of them. */
  stepSubtitle(step: RecipePreviewStep): string | null {
    const parts: string[] = [];
    if (step.device) parts.push(COOK_DEVICE_LABELS[step.device] ?? step.device);
    if (step.temp_c != null) parts.push(`${step.temp_c}°C`);
    if (step.heat_level != null) parts.push(`Stufe ${step.heat_level}`);
    if (step.time_min != null) parts.push(`${step.time_min} Min.`);
    if (step.rack_position != null) parts.push(`Einschub ${step.rack_position}`);
    if (step.preheat) parts.push('vorheizen');
    if (step.location) parts.push(WAIT_LOCATION_LABELS[step.location] ?? step.location);
    if (step.covered) parts.push('abgedeckt');
    return parts.length ? parts.join(' · ') : null;
  }
}
